/*

PW Simple Tooltips

This wrapper component adds behavior to automatically display a styled tooltip for any of
its descendant elements which have a `data-tooltip` attribute, when they are
moused over. The tooltip is inserted as a child of the main body and aligned to the
moused-over element; the alignment is to the top-right by default but is automatically
flipped as necessary to keep it within the bounds of the viewport.

Additional configuration attributes:

`data-tooltip-class="..."` adds an additional className to the tooltip element. Can be
declared on the same element as `data-tooltip`, or any ancestor element and it will be
inherited so you can easily style all tooltips within a container at once.

`data-tooltip-align="top-right|top-left|bottom-right|bottom-left"` allows a *preferred*
tooltip alignment to be declared. The preferred alignment may still be flipped if it hits
the edges of the viewport.

`data-tooltip-follow="true"` causes the tooltip to follow the mouse cursor position as
it moves around within the element.

*/

import React from 'react'
import T from 'prop-types'
import ReactDOM from 'react-dom'
import Tooltip from './Tooltip'
import { generateId } from '../utils'

const OVER_EVENTS = ['mouseover', 'touchstart', 'focusin']
const OUT_EVENTS = ['mouseout', 'touchend', 'focusout']
const MOVE_EVENTS = ['mousemove', 'touchmove']
const GLOBAL_HIDE_EVENTS = ['mousewheel', 'mousedown']

const MOUNT_NODE_ID = 'pw_tooltip_simple_mount_node'
const getMountNode = () => document.getElementById(MOUNT_NODE_ID)

export default class SimpleTooltips extends React.Component {
  static displayName = 'SimpleTooltips'

  static propTypes = {
    children: T.node
  }

  componentDidMount() {
    const el = ReactDOM.findDOMNode(this)
    OVER_EVENTS.forEach(name => {
      el.addEventListener(name, this._sttOnMouseOver, false)
    })
    OUT_EVENTS.forEach(name => {
      el.addEventListener(name, this._sttOnMouseOut, false)
    })
    GLOBAL_HIDE_EVENTS.forEach(name =>
      window.addEventListener(name, this._sttDestroyTooltip, true)
    )
  }

  componentWillUnmount() {
    let el = ReactDOM.findDOMNode(this)
    OVER_EVENTS.forEach(name => {
      el.removeEventListener(name, this._sttOnMouseOver, false)
    })
    OUT_EVENTS.forEach(name => {
      el.removeEventListener(name, this._sttOnMouseOut, false)
    })
    MOVE_EVENTS.forEach(name => {
      el.removeEventListener(name, this._sttOnMouseMove, false)
    })
    GLOBAL_HIDE_EVENTS.forEach(name => {
      window.removeEventListener(name, this._sttDestroyTooltip, true)
    })

    el = getMountNode()
    if (el) {
      el.parentNode.removeChild(el)
    }
  }

  _getInheritedAttr = (node, attrName) => {
    while (node) {
      if (node.getAttribute) {
        const attrVal = node.getAttribute(attrName)
        if (attrVal) return attrVal
      }
      node = node.parentNode
    }
  }

  _sttOnMouseOver = e => {
    let node = e.target
    while (node) {
      const tooltip = node.getAttribute && node.getAttribute('data-tooltip')
      if (tooltip) {
        this._overNode = node
        if (node.getAttribute('data-tooltip-follow') === 'true') {
          MOVE_EVENTS.forEach(name => {
            ReactDOM.findDOMNode(this).addEventListener(name, this._sttOnMouseMove, false)
          })
        }
        this._sttShowTooltip(
          node,
          tooltip,
          this._getInheritedAttr(node, 'data-tooltip-class')
        )
        return
      }
      node = node.parentNode
    }
  }

  _sttOnMouseMove = e => {
    const overNode = this._overNode
    if (overNode && overNode.contains && !overNode.contains(e.relatedTarget) && this._sttNode) {
      this._sttNode.setPosition({
        left: e.pageX,
        top: e.pageY,
        right: e.pageX,
        bottom: e.pageY,
        height: 0,
        width: 0
      })
    }
  }

  _sttOnMouseOut = e => {
    const overNode = this._overNode
    if (overNode && (!overNode.contains || !overNode.contains(e.relatedTarget))) {
      this._sttDestroyTooltip()
    }
  }

  _sttDestroyTooltip = () => {
    ReactDOM.findDOMNode(this).removeEventListener(
      'mousemove',
      this._sttOnMouseMove,
      false
    )
    const mountNode = getMountNode()
    if (mountNode) {
      ReactDOM.unmountComponentAtNode(mountNode)
    }
    if (this._overNode) {
      this._overNode.removeAttribute('aria-describedby')
    }
    delete this._overNode
    delete this._sttNode
  }

  _sttShowTooltip = (el, content, className /*, followPointer = false*/) => {
    let mountNode = getMountNode()
    if (!mountNode) {
      mountNode = document.createElement('div')
      mountNode.id = MOUNT_NODE_ID
      mountNode.className = 'pw_tooltip_simple_mount'
      document.body.appendChild(mountNode)
    }

    const ttId = generateId('simple_tooltip')
    const rect = el.getBoundingClientRect()
    const preferredAlign = el.getAttribute('data-tooltip-align') || 'top-right'
    this._sttNode = ReactDOM.render(
      <Tooltip
        id={ttId}
        className={className}
        preferredAlign={preferredAlign}
        rect={rect}
      >
        {content}
      </Tooltip>,
      mountNode
    )
    el.setAttribute('aria-describedby', ttId)
  }

  render() {
    return React.Children.only(this.props.children)
  }
}
